import { useEffect, useRef } from 'react';

/**
 * A video in a modal, opened from a poster on the page.
 *
 * The player is mounted only while `open` — not hidden, absent. That is what
 * stops playback when the dialog closes, and it means the video's bytes are
 * never fetched by someone who does not ask to watch it.
 *
 * Built on the native `<dialog>` for the same reasons as {@link RegisterDialog}:
 * focus trapping, Escape, inertness, and the top layer come from the browser.
 */

interface VideoLightboxProps {
  open: boolean;
  onClose: () => void;
  /** Absolute URL of the video file. */
  src: string;
  /** Frame shown while the first bytes arrive. */
  poster: string;
  /** Names the dialog for screen readers and the player for assistive tech. */
  title: string;
}

export function VideoLightbox({ open, onClose, src, poster, title }: VideoLightboxProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  /** Element that had focus before opening, so it can be restored on close. */
  const openerRef = useRef<Element | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open && !dialog.open) {
      openerRef.current = document.activeElement;
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  const handleClose = () => {
    const opener = openerRef.current;
    if (opener instanceof HTMLElement) opener.focus();
    onClose();
  };

  return (
    <dialog
      ref={dialogRef}
      className="fias-video-dialog"
      aria-label={title}
      onClose={handleClose}
      onClick={(event) => {
        if (event.target === dialogRef.current) onClose();
      }}
    >
      <div className="fias-video-panel">
        <button
          type="button"
          onClick={onClose}
          className="fias-video-close"
          aria-label="Close the video"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden>
            <path d="m6 6 12 12M18 6 6 18" strokeLinecap="round" />
          </svg>
        </button>

        <div className="fias-video-frame">
          {/* Autoplay is allowed here because the dialog only opens from a
              click — the user gesture browsers require before playing sound.
              If a browser refuses anyway, the controls are already on screen. */}
          {/* No caption track exists for this master. The section's copy beside
              the poster carries the same claims in text. */}
          {open ? (
            <video src={src} poster={poster} title={title} controls autoPlay playsInline />
          ) : null}
        </div>
      </div>
    </dialog>
  );
}
